import React, { useState, useEffect, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";
import SEO from "@/components/SEO";
import {
  Briefcase,
  MapPin,
  Clock,
  Money,
  Buildings,
  MagnifyingGlass,
  Funnel,
  CaretDown,
  Plus,
  Calendar,
  Users,
  Tractor,
  Cow,
  Plant,
  Wrench
} from "@phosphor-icons/react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const JOB_CATEGORIES = [
  { value: "all", label: "All Categories", icon: Briefcase },
  { value: "farm_worker", label: "Farm Worker", icon: Tractor },
  { value: "dairy", label: "Dairy", icon: Cow },
  { value: "tillage", label: "Tillage", icon: Plant },
  { value: "livestock", label: "Livestock", icon: Cow },
  { value: "management", label: "Farm Management", icon: Users },
  { value: "machinery", label: "Machinery/Mechanics", icon: Wrench },
  { value: "seasonal", label: "Seasonal Work", icon: Calendar },
  { value: "agri_business", label: "Agri-Business", icon: Buildings }
];

const JOB_TYPES = [
  { value: "all", label: "All Types" },
  { value: "full_time", label: "Full Time" },
  { value: "part_time", label: "Part Time" },
  { value: "seasonal", label: "Seasonal" },
  { value: "contract", label: "Contract" }
];

const COUNTIES = [
  "All Ireland", "Carlow", "Cavan", "Clare", "Cork", "Donegal", "Dublin",
  "Galway", "Kerry", "Kildare", "Kilkenny", "Laois", "Leitrim", "Limerick",
  "Longford", "Louth", "Mayo", "Meath", "Monaghan", "Offaly", "Roscommon",
  "Sligo", "Tipperary", "Waterford", "Westmeath", "Wexford", "Wicklow"
];

export default function FarmJobsPage() {
  const { user, token } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  
  // Filters
  const [category, setCategory] = useState(searchParams.get("category") || "all");
  const [jobType, setJobType] = useState(searchParams.get("type") || "all");
  const [county, setCounty] = useState(searchParams.get("county") || "All Ireland");
  const [searchQuery, setSearchQuery] = useState(searchParams.get("q") || "");
  const [showFilters, setShowFilters] = useState(false);

  const fetchJobs = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page,
        limit: 15,
        ...(category !== "all" && { category }),
        ...(jobType !== "all" && { job_type: jobType }),
        ...(county !== "All Ireland" && { county }),
        ...(searchQuery && { search: searchQuery })
      };
      
      const response = await axios.get(`${API}/farm-jobs`, { params });
      setJobs(response.data.jobs);
      setTotalCount(response.data.total);
    } catch (error) {
      console.error("Error fetching jobs:", error);
      toast.error("Failed to load jobs");
    } finally {
      setLoading(false);
    }
  }, [page, category, jobType, county, searchQuery]);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  const applyFilters = () => {
    setPage(1);
    const params = new URLSearchParams();
    if (category !== "all") params.set("category", category);
    if (jobType !== "all") params.set("type", jobType);
    if (county !== "All Ireland") params.set("county", county);
    if (searchQuery) params.set("q", searchQuery);
    setSearchParams(params);
  };

  const formatSalary = (job) => {
    if (job.salary_type === "hourly") {
      return `€${job.salary_min}${job.salary_max ? `-€${job.salary_max}` : ""}/hr`;
    }
    if (job.salary_type === "daily") {
      return `€${job.salary_min}${job.salary_max ? `-€${job.salary_max}` : ""}/day`;
    }
    // Annual
    const min = job.salary_min >= 1000 ? `€${(job.salary_min / 1000).toFixed(0)}k` : `€${job.salary_min}`;
    const max = job.salary_max ? (job.salary_max >= 1000 ? `€${(job.salary_max / 1000).toFixed(0)}k` : `€${job.salary_max}`) : null;
    return max ? `${min} - ${max}` : min;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "Yesterday";
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
  };

  const getCategoryIcon = (cat) => {
    const found = JOB_CATEGORIES.find(c => c.value === cat);
    const Icon = found?.icon || Briefcase;
    return <Icon size={18} />;
  };

  return (
    <div className="min-h-screen bg-[#FFF1E5]">
      <SEO title="Farm Jobs" description="Find agricultural jobs across Ireland and the UK - dairy, tillage, livestock, farm management and machinery roles. Post jobs free." url="/jobs" />
      <Header />
      
      {/* Hero */}
      <section className="bg-gradient-to-r from-[#022C22] to-[#064E3B] py-8">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-2">
            <span className="px-3 py-1 bg-[#059669] text-white text-xs font-mono uppercase tracking-wider">
              Jobs
            </span>
            <span className="px-3 py-1 bg-[#FBBF24] text-[#022C22] text-xs font-mono uppercase tracking-wider">
              Farm Careers
            </span>
          </div>
          <h1 className="font-serif text-3xl sm:text-4xl text-white mb-2">
            Farm Jobs Board
          </h1>
          <p className="text-[#A7F3D0] text-lg">
            Find agricultural jobs and careers across Ireland
          </p>
        </div>
      </section>

      <main className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search Bar */}
        <div className="bg-white border border-[#E0D4C8] p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <MagnifyingGlass size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B6661]" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search jobs, skills, companies..."
                className="w-full pl-10 pr-4 py-2 border border-[#E0D4C8] rounded focus:outline-none focus:ring-2 focus:ring-[#0F766E]"
              />
            </div>
            <select
              value={county}
              onChange={(e) => setCounty(e.target.value)}
              className="px-4 py-2 border border-[#E0D4C8] rounded focus:outline-none focus:ring-2 focus:ring-[#0F766E]"
            >
              {COUNTIES.map(c => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 px-4 py-2 border border-[#E0D4C8] rounded hover:bg-[#F9FAFB]"
            >
              <Funnel size={18} />
              Filters
              <CaretDown size={14} className={`transition-transform ${showFilters ? "rotate-180" : ""}`} />
            </button>
            <button
              onClick={applyFilters}
              className="px-6 py-2 bg-[#0F766E] text-white font-medium hover:bg-[#064E3B] transition-colors"
            >
              Search
            </button>
          </div>

          {/* Expanded Filters */}
          {showFilters && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-4 pt-4 border-t border-[#E0D4C8]">
              <div>
                <label className="block text-xs text-[#6B6661] mb-1">Category</label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="w-full px-3 py-2 border border-[#E0D4C8] rounded text-sm"
                >
                  {JOB_CATEGORIES.map(c => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs text-[#6B6661] mb-1">Job Type</label>
                <select
                  value={jobType}
                  onChange={(e) => setJobType(e.target.value)}
                  className="w-full px-3 py-2 border border-[#E0D4C8] rounded text-sm"
                >
                  {JOB_TYPES.map(t => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>

        {/* Results Header */}
        <div className="flex items-center justify-between mb-6">
          <p className="text-[#6B6661]">
            {totalCount} {totalCount === 1 ? "job" : "jobs"} found
          </p>
          <Link
            to="/farm-jobs/post"
            className="flex items-center gap-2 px-4 py-2 bg-[#0F766E] text-white text-sm font-medium hover:bg-[#064E3B] transition-colors"
          >
            <Plus size={18} />
            Post a Job
          </Link>
        </div>

        {/* Jobs List */}
        {loading ? (
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="bg-white border border-[#E0D4C8] p-6 animate-pulse">
                <div className="h-6 bg-[#E5E7EB] w-2/3 mb-3"></div>
                <div className="h-4 bg-[#E5E7EB] w-1/3 mb-2"></div>
                <div className="h-4 bg-[#E5E7EB] w-1/2"></div>
              </div>
            ))}
          </div>
        ) : jobs.length === 0 ? (
          <div className="text-center py-12 bg-white border border-[#E0D4C8]">
            <Briefcase size={48} className="mx-auto text-[#E0D4C8] mb-4" />
            <h3 className="font-serif text-xl text-[#33302E] mb-2">No jobs found</h3>
            <p className="text-[#6B6661]">Try adjusting your filters or check back later</p>
          </div>
        ) : (
          <div className="space-y-4">
            {jobs.map((job) => (
              <Link
                key={job.id}
                to={`/farm-jobs/${job.id}`}
                className="block bg-white border border-[#E0D4C8] p-6 hover:border-[#0F766E] hover:shadow-md transition-all group"
              >
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="p-2 bg-[#F0FDF4] rounded">
                        {getCategoryIcon(job.category)}
                      </div>
                      <div>
                        <h3 className="font-serif text-xl text-[#33302E] group-hover:text-[#0F766E] transition-colors">
                          {job.title}
                        </h3>
                        <p className="text-sm text-[#6B6661]">{job.company_name}</p>
                      </div>
                      {job.featured && (
                        <span className="px-2 py-1 bg-[#FBBF24] text-[#022C22] text-xs font-medium">
                          Featured
                        </span>
                      )}
                      {job.urgent && (
                        <span className="px-2 py-1 bg-[#DC2626] text-white text-xs font-medium">
                          Urgent
                        </span>
                      )}
                    </div>
                    
                    <p className="text-[#6B6661] text-sm mb-3 line-clamp-2">
                      {job.description}
                    </p>
                    
                    <div className="flex flex-wrap items-center gap-4 text-sm text-[#6B6661]">
                      <span className="flex items-center gap-1">
                        <MapPin size={14} />
                        {job.county}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock size={14} />
                        {job.job_type?.replace("_", " ")}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar size={14} />
                        {formatDate(job.posted_at)}
                      </span>
                    </div>
                  </div>
                  
                  <div className="text-right">
                    <div className="font-serif text-xl text-[#0F766E] mb-1">
                      {formatSalary(job)}
                    </div>
                    <span className="text-xs text-[#6B6661]">
                      {job.salary_type === "hourly" ? "per hour" : 
                       job.salary_type === "daily" ? "per day" : "per year"}
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalCount > 15 && (
          <div className="flex justify-center gap-2 mt-8">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-4 py-2 border border-[#E0D4C8] disabled:opacity-50 hover:bg-[#F9FAFB]"
            >
              Previous
            </button>
            <span className="px-4 py-2 text-[#6B6661]">
              Page {page} of {Math.ceil(totalCount / 15)}
            </span>
            <button
              onClick={() => setPage(p => p + 1)}
              disabled={page >= Math.ceil(totalCount / 15)}
              className="px-4 py-2 border border-[#E0D4C8] disabled:opacity-50 hover:bg-[#F9FAFB]"
            >
              Next
            </button>
          </div>
        )}

        {/* Employer CTA */}
        <div className="mt-12 bg-gradient-to-r from-[#022C22] to-[#064E3B] p-8">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="font-serif text-2xl text-white mb-3">
              Hiring Farm Workers?
            </h2>
            <p className="text-[#A7F3D0] mb-6">
              Post your job and reach thousands of agricultural workers across Ireland.
              Featured listings appear at the top and get 10x more applications.
            </p>
            <div className="flex justify-center gap-4">
              <Link
                to="/farm-jobs/post"
                className="px-6 py-3 bg-white text-[#022C22] font-medium hover:bg-[#F0FDF4] transition-colors"
              >
                Post Free Job
              </Link>
              <Link
                to="/advertise"
                className="px-6 py-3 border-2 border-[#FBBF24] text-[#FBBF24] font-medium hover:bg-[#FBBF24] hover:text-[#022C22] transition-colors"
              >
                Featured Posting - €29
              </Link>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
